/**
 * VideoDocsPage — documentazione del gestionale (rotta /documentazione).
 *
 * Tre tab, una per lavoro:
 *  · «novità e articoli» → la biblioteca unificata `kb_contents`: si scrive un
 *    annuncio, lo si pubblica, e il giorno dopo compare in home a chi di dovere.
 *    È la risposta al problema che ha originato il progetto (il noleggio
 *    operativo rilasciato a luglio 2026 e mai spiegato a nessuno).
 *  · «video» → la gestione di `platform_docs_chapters` / `platform_docs_videos`,
 *    IDENTICA a prima e IN PRODUZIONE: capitoli, upload MP4, stati. Il formato
 *    `video` di `kb_contents` esiste già nel modello ma verrà assorbito solo in
 *    fase 4: fino a quel giorno questa tab è l'unica strada e non si tocca.
 *  · «manutenzione» → le due liste che DEVONO restare vuote (contenuti senza
 *    responsabile, contenuti con la revisione scaduta). Nessuna architettura
 *    garantisce che qualcuno aggiorni un articolo: la sola difesa è guardare.
 *
 * Doc canonici: docs/01-ARCHITECTURE/KB-CONTENT-MODEL.md (novità e articoli)
 *               docs/01-ARCHITECTURE/VIDEO-DOCS-MODEL.md (video)
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, ListItemText, Menu,
  MenuItem, Paper, Snackbar, Stack, Tab, Table, TableBody, TableCell, TableHead,
  TableRow, Tabs, TextField, Tooltip, Typography, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle,
} from '@mui/material';
import {
  Add, ArchiveOutlined, CampaignOutlined, EditOutlined, HealthAndSafetyOutlined,
  LibraryBooks, PlayCircleOutline, PublishOutlined, Refresh, SwapHoriz,
  StopCircleOutlined, VideoLibrary,
} from '@mui/icons-material';
import { tokens } from '../theme';
import { SoftChip } from '../components/SoftChip';
import { ChaptersDialog } from '../components/video-docs/ChaptersDialog';
import { VideoFormDialog } from '../components/video-docs/VideoFormDialog';
import {
  STATO_VIDEO_HINTS,
  STATO_VIDEO_LABELS,
  STATO_VIDEO_ORDER,
  STATO_VIDEO_TONES,
  formatDurataVideo,
} from '../components/video-docs/labels';
import {
  distinctAreeCoperte,
  getVideoDownloadUrl,
  listChapters,
  listVideos,
  updateVideo,
} from '../services/videoDocsService';
import type {
  PlatformDocsChapter,
  PlatformDocsVideo,
  VideoDocStato,
} from '../types/videoDocs';
import { KbContenutoFormDialog } from '../components/kb/KbContenutoFormDialog';
import { PubblicaDialog } from '../components/kb/PubblicaDialog';
import {
  KB_FORMATO_TONE,
  KB_FRESCHEZZA_TONE,
  KB_LIVELLO_TONE,
  KB_STATO_HINT,
  KB_STATO_TONE,
  formattaData,
} from '../components/kb/labels';
import {
  archiviaKbContenuto,
  caricaPlatea,
  contenutiScaduti,
  contenutiSenzaOwner,
  filtraContenuti,
  getCorpoDaModificare,
  getCorpoPubblicato,
  getKbGovernance,
  listKbAreas,
  listKbContents,
} from '../services/kbContentsService';
import {
  KB_FORMATI,
  KB_FORMATO_LABEL,
  KB_FRESCHEZZA_LABEL,
  KB_LIVELLO_LABEL,
  KB_STATI,
  KB_STATO_LABEL,
  KB_GOVERNANCE_DEFAULT,
} from '@shared/kb/kbContentModel';
import { formattaVersione } from '@shared/kb/kbVersioning';
import { aMillis } from '@shared/kb/kbTypes';
import type { KbArea, KbContenuto } from '@shared/kb/kbTypes';
import {
  KB_FILTRO_TUTTI,
  type KbCorpoCaricato,
  type KbFiltriContenuti,
  type KbGovernanceCaricata,
  type KbPlatea,
} from '../types/kb';

type TabAttiva = 'contenuti' | 'video' | 'manutenzione';

/** Bersagli touch: la console si usa anche da tablet. */
const TOUCH_MIN = 40;

type Snack = { msg: string; severity: 'success' | 'error' } | null;

// ============================================================================
// TAB «NOVITÀ E ARTICOLI» — `kb_contents`
// ============================================================================

interface TabContenutiProps {
  contenuti: KbContenuto[];
  aree: KbArea[];
  platea: KbPlatea | null;
  errorePlatea: string | null;
  governance: KbGovernanceCaricata;
  caricamento: boolean;
  errore: string | null;
  onRicarica: () => void;
  showSnack: (msg: string, severity: 'success' | 'error') => void;
}

const TabContenuti: React.FC<TabContenutiProps> = ({
  contenuti, aree, platea, errorePlatea, governance, caricamento, errore,
  onRicarica, showSnack,
}) => {
  const [filtri, setFiltri] = useState<KbFiltriContenuti>({
    formato: KB_FILTRO_TUTTI,
    stato: KB_FILTRO_TUTTI,
    testo: '',
  });

  // Editor
  const [formOpen, setFormOpen] = useState(false);
  const [formTarget, setFormTarget] = useState<KbContenuto | null>(null);
  const [corpoTarget, setCorpoTarget] = useState<KbCorpoCaricato | null>(null);
  const [aperturaInCorso, setAperturaInCorso] = useState<string | null>(null);

  // Pubblicazione
  const [pubOpen, setPubOpen] = useState(false);
  const [pubTarget, setPubTarget] = useState<KbContenuto | null>(null);
  const [pubCorpo, setPubCorpo] = useState('');
  const [pubCorpoPubblicato, setPubCorpoPubblicato] = useState<KbCorpoCaricato | null>(null);

  // Archiviazione (conferma esplicita: niente alert nativi del browser)
  const [archivioTarget, setArchivioTarget] = useState<KbContenuto | null>(null);
  const [archiviazioneInCorso, setArchiviazioneInCorso] = useState(false);

  const visibili = useMemo(() => filtraContenuti(contenuti, filtri), [contenuti, filtri]);

  const apriNuovo = () => {
    setFormTarget(null);
    setCorpoTarget(null);
    setFormOpen(true);
  };

  const apriModifica = async (c: KbContenuto) => {
    setAperturaInCorso(c.id);
    try {
      const corpo = await getCorpoDaModificare(c);
      setFormTarget(c);
      setCorpoTarget(corpo);
      setFormOpen(true);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Errore caricamento del testo', 'error');
    } finally {
      setAperturaInCorso(null);
    }
  };

  const apriPubblica = async (c: KbContenuto) => {
    setAperturaInCorso(c.id);
    try {
      const [daPubblicare, pubblicato] = await Promise.all([
        getCorpoDaModificare(c),
        getCorpoPubblicato(c),
      ]);
      setPubTarget(c);
      setPubCorpo(daPubblicare?.corpo_markdown ?? '');
      setPubCorpoPubblicato(pubblicato);
      setPubOpen(true);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Errore caricamento delle revisioni', 'error');
    } finally {
      setAperturaInCorso(null);
    }
  };

  const confermaArchiviazione = async () => {
    const target = archivioTarget;
    if (!target) return;
    setArchiviazioneInCorso(true);
    try {
      await archiviaKbContenuto({ content_id: target.id });
      showSnack(`«${target.titolo}» archiviato`, 'success');
      setArchivioTarget(null);
      onRicarica();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Errore archiviazione', 'error');
    } finally {
      setArchiviazioneInCorso(false);
    }
  };

  return (
    <Box>
      {/* AZIONI E FILTRI */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ mb: 2 }}
      >
        <TextField
          label="Cerca"
          value={filtri.testo}
          onChange={(e) => setFiltri((f) => ({ ...f, testo: e.target.value }))}
          placeholder="titolo, indirizzo o riassunto"
          sx={{ flex: 1, minWidth: 220 }}
        />
        <TextField
          select
          label="Formato"
          value={filtri.formato}
          onChange={(e) =>
            setFiltri((f) => ({ ...f, formato: e.target.value as KbFiltriContenuti['formato'] }))
          }
          sx={{ minWidth: 150 }}
        >
          <MenuItem value={KB_FILTRO_TUTTI} sx={{ minHeight: TOUCH_MIN }}>tutti</MenuItem>
          {KB_FORMATI.map((f) => (
            <MenuItem key={f} value={f} sx={{ minHeight: TOUCH_MIN }}>
              {KB_FORMATO_LABEL[f]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Stato"
          value={filtri.stato}
          onChange={(e) =>
            setFiltri((f) => ({ ...f, stato: e.target.value as KbFiltriContenuti['stato'] }))
          }
          sx={{ minWidth: 170 }}
        >
          <MenuItem value={KB_FILTRO_TUTTI} sx={{ minHeight: TOUCH_MIN }}>tutti</MenuItem>
          {KB_STATI.map((s) => (
            <MenuItem key={s} value={s} sx={{ minHeight: TOUCH_MIN }}>
              {KB_STATO_LABEL[s]}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title="Ricarica">
          <IconButton
            onClick={onRicarica}
            disabled={caricamento}
            sx={{ width: TOUCH_MIN, height: TOUCH_MIN }}
          >
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={apriNuovo}
          // Disabilitato finché le aree e la governance non sono arrivate: un
          // contenuto nuovo prende da lì i valori di partenza (giorni di
          // evidenza, canali, periodicità di revisione).
          disabled={caricamento}
          sx={{ minHeight: TOUCH_MIN, touchAction: 'manipulation', flexShrink: 0 }}
        >
          Nuovo contenuto
        </Button>
      </Stack>

      {errore && <Alert severity="error" sx={{ mb: 2 }}>{errore}</Alert>}

      {caricamento ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderColor: tokens.color.line }}>
          <CircularProgress size={28} />
        </Paper>
      ) : visibili.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderColor: tokens.color.line }}>
          <Typography
            sx={{
              fontFamily: tokens.font.display,
              fontSize: '1.05rem',
              fontStyle: 'italic',
              color: tokens.color.mist,
            }}
          >
            {contenuti.length === 0
              ? 'Nessun contenuto: il primo annuncio si scrive con "Nuovo contenuto".'
              : 'Nessun contenuto con questi filtri.'}
          </Typography>
        </Paper>
      ) : (
        <Paper
          variant="outlined"
          sx={{ borderColor: tokens.color.line, bgcolor: tokens.color.paper, overflowX: 'auto' }}
        >
          <Table size="small" sx={{ '& td': { py: 1.25 }, '& tr:last-child td': { borderBottom: 0 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Titolo</TableCell>
                <TableCell>Formato</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell>Versione</TableCell>
                <TableCell>Allineamento</TableCell>
                <TableCell>Aree</TableCell>
                <TableCell align="right" sx={{ pr: 2 }}>Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibili.map((c) => {
                const occupato = aperturaInCorso === c.id;
                return (
                  <TableRow key={c.id} hover>
                    <TableCell sx={{ minWidth: 260 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: tokens.color.graphite, lineHeight: 1.3 }}
                      >
                        {c.titolo || '(senza titolo)'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: tokens.color.mist,
                          fontFamily: tokens.font.mono,
                          display: 'block',
                        }}
                      >
                        {c.id}
                      </Typography>
                      {c.formato === 'novita' && c.annuncio && (
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                          <SoftChip
                            label={KB_LIVELLO_LABEL[c.annuncio.livello_impatto]}
                            tone={KB_LIVELLO_TONE[c.annuncio.livello_impatto]}
                          />
                          <SoftChip
                            label={`evidenza fino al ${formattaData(aMillis(c.annuncio.scadenza_evidenza_at))}`}
                            tone="default"
                          />
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      <SoftChip label={KB_FORMATO_LABEL[c.formato]} tone={KB_FORMATO_TONE[c.formato]} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={KB_STATO_HINT[c.stato]}>
                        <Box component="span">
                          <SoftChip label={KB_STATO_LABEL[c.stato]} tone={KB_STATO_TONE[c.stato]} />
                        </Box>
                      </Tooltip>
                      {c.revisione_bozza != null &&
                        c.revisione_bozza !== c.revisione_pubblicata && (
                          <Box sx={{ mt: 0.5 }}>
                            <SoftChip label="bozza più recente" tone="info" />
                          </Box>
                        )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: tokens.font.mono, color: tokens.color.ash }}
                      >
                        {formattaVersione(c.versione_maggiore, c.versione_minore)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <SoftChip
                        label={KB_FRESCHEZZA_LABEL[c.stato_freschezza ?? 'attuale']}
                        tone={KB_FRESCHEZZA_TONE[c.stato_freschezza ?? 'attuale']}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                        {(c.aree_coperte ?? []).map((area) => (
                          <Chip
                            key={area}
                            size="small"
                            label={area}
                            variant="outlined"
                            sx={{ fontFamily: tokens.font.mono, fontSize: '0.65rem', height: 20 }}
                          />
                        ))}
                        {(c.aree_coperte ?? []).length === 0 && (
                          <Typography variant="caption" sx={{ color: tokens.color.mist }}>—</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 2 }}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Modifica">
                          <span>
                            <IconButton
                              disabled={occupato}
                              onClick={() => apriModifica(c)}
                              sx={{ width: TOUCH_MIN, height: TOUCH_MIN }}
                            >
                              {occupato ? <CircularProgress size={16} /> : <EditOutlined fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        {/* Pubblicabile solo se esiste una BOZZA: è la
                            precondizione della callable (`revisione_bozza`
                            viene azzerata dalla pubblicazione). Disabilitare
                            qui evita di far scoprire il vincolo con un errore
                            a viaggio completato. */}
                        <Tooltip
                          title={
                            c.stato === 'archiviato'
                              ? 'Contenuto archiviato: riprendilo come bozza per pubblicarlo'
                              : c.revisione_bozza == null
                                ? 'Niente da pubblicare: salva prima una modifica'
                                : 'Pubblica'
                          }
                        >
                          <span>
                            <IconButton
                              disabled={
                                occupato || c.stato === 'archiviato' || c.revisione_bozza == null
                              }
                              onClick={() => apriPubblica(c)}
                              sx={{ width: TOUCH_MIN, height: TOUCH_MIN, color: tokens.color.success }}
                            >
                              <PublishOutlined fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Archivia">
                          <span>
                            <IconButton
                              disabled={occupato || c.stato === 'archiviato'}
                              onClick={() => setArchivioTarget(c)}
                              sx={{ width: TOUCH_MIN, height: TOUCH_MIN }}
                            >
                              <ArchiveOutlined fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* EDITOR */}
      <KbContenutoFormDialog
        open={formOpen}
        contenuto={formTarget}
        corpoIniziale={corpoTarget}
        aree={aree}
        contenutiEsistenti={contenuti}
        platea={platea}
        errorePlatea={errorePlatea}
        governance={governance}
        onClose={() => setFormOpen(false)}
        onSalvato={() => {
          showSnack(formTarget ? 'Bozza aggiornata' : 'Bozza creata', 'success');
          onRicarica();
        }}
      />

      {/* PUBBLICAZIONE */}
      <PubblicaDialog
        open={pubOpen}
        contenuto={pubTarget}
        corpoDaPubblicare={pubCorpo}
        corpoPubblicato={pubCorpoPubblicato}
        onClose={() => setPubOpen(false)}
        onPubblicato={(esito) => {
          showSnack(
            `Pubblicato in versione ${formattaVersione(esito.versione_maggiore, esito.versione_minore)}`,
            'success',
          );
          onRicarica();
        }}
      />

      {/* CONFERMA ARCHIVIAZIONE */}
      <Dialog
        open={archivioTarget !== null}
        onClose={archiviazioneInCorso ? undefined : () => setArchivioTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600 }}>
          Archiviare questo contenuto?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            «{archivioTarget?.titolo}» non si mostrerà più a nessuno. Lo storico delle revisioni
            resta, e il contenuto si può riprendere come bozza.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setArchivioTarget(null)}
            disabled={archiviazioneInCorso}
            sx={{ minHeight: TOUCH_MIN }}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confermaArchiviazione}
            disabled={archiviazioneInCorso}
            startIcon={
              archiviazioneInCorso ? <CircularProgress size={14} color="inherit" /> : <ArchiveOutlined />
            }
            sx={{ minHeight: TOUCH_MIN, touchAction: 'manipulation' }}
          >
            Archivia
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ============================================================================
// TAB «MANUTENZIONE» — le liste che devono restare vuote
// ============================================================================

/** Una lista di manutenzione: il titolo, il perché, e le righe (spesso zero). */
const BloccoManutenzione: React.FC<{
  titolo: string;
  spiegazione: string;
  righe: KbContenuto[];
  colonna: string;
  valore: (c: KbContenuto) => string;
}> = ({ titolo, spiegazione, righe, colonna, valore }) => (
    <Paper variant="outlined" sx={{ borderColor: tokens.color.line, overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${tokens.color.lineSoft}` }}
      >
        <Typography
          sx={{
            fontFamily: tokens.font.display,
            fontWeight: 600,
            fontSize: '1.05rem',
            color: tokens.color.graphite,
          }}
        >
          {titolo}
        </Typography>
        <SoftChip
          label={righe.length === 0 ? 'tutto in regola' : `${righe.length} da sistemare`}
          tone={righe.length === 0 ? 'success' : 'warning'}
        />
      </Stack>
      <Box sx={{ px: 2, pt: 1.5 }}>
        <Typography variant="caption" sx={{ color: tokens.color.mist }}>
          {spiegazione}
        </Typography>
      </Box>
      {righe.length === 0 ? (
        <Box sx={{ px: 2, py: 2.5 }}>
          <Typography
            sx={{
              fontFamily: tokens.font.display,
              fontStyle: 'italic',
              fontSize: '0.95rem',
              color: tokens.color.mist,
            }}
          >
            Niente da fare qui.
          </Typography>
        </Box>
      ) : (
        <Table size="small" sx={{ mt: 1.5, '& tr:last-child td': { borderBottom: 0 } }}>
          <TableHead>
            <TableRow>
              <TableCell>Contenuto</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell>{colonna}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {righe.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.graphite }}>
                    {c.titolo || '(senza titolo)'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: tokens.color.mist, fontFamily: tokens.font.mono }}
                  >
                    {c.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <SoftChip label={KB_STATO_LABEL[c.stato]} tone={KB_STATO_TONE[c.stato]} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: tokens.color.ash }}>
                    {valore(c)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
);

interface TabManutenzioneProps {
  contenuti: KbContenuto[];
  platea: KbPlatea | null;
  caricamento: boolean;
}

const TabManutenzione: React.FC<TabManutenzioneProps> = ({ contenuti, platea, caricamento }) => {
  const senzaOwner = useMemo(() => contenutiSenzaOwner(contenuti), [contenuti]);
  // `Date.now()` alla costruzione della lista: la scadenza è al giorno, non al
  // millisecondo, e un timer che ricalcola non aggiungerebbe informazione.
  const scaduti = useMemo(() => contenutiScaduti(contenuti, Date.now()), [contenuti]);

  const nomeUtente = (uid: string | undefined) => {
    if (!uid) return '—';
    return platea?.utenti.find((u) => u.uid === uid)?.etichetta ?? uid;
  };

  if (caricamento) {
    return (
      <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderColor: tokens.color.line }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  return (
    <Stack spacing={2.5}>
      <BloccoManutenzione
        titolo="Contenuti senza responsabile"
        spiegazione="Pubblicare resta un lavoro umano: un contenuto senza owner è un contenuto che nessuno aggiornerà. Questa lista deve restare vuota."
        righe={senzaOwner}
        colonna="Ultima modifica"
        valore={(c) => formattaData(aMillis(c.updated_at) ?? aMillis(c.created_at))}
      />
      <BloccoManutenzione
        titolo="Revisione periodica scaduta"
        spiegazione="La scadenza è calcolata dal sistema sui giorni indicati nel contenuto. Un contenuto senza giorni indicati non compare qui: non è in regola, semplicemente non ha una scadenza dichiarata."
        righe={scaduti}
        colonna="Responsabile"
        valore={(c) => nomeUtente(c.owner_uid)}
      />
    </Stack>
  );
};

// ============================================================================
// PAGINA
// ============================================================================

export const VideoDocsPage: React.FC = () => {
  const [tab, setTab] = useState<TabAttiva>('contenuti');
  const [snack, setSnack] = useState<Snack>(null);
  const showSnack = (msg: string, severity: 'success' | 'error') => setSnack({ msg, severity });

  // ── dati della biblioteca `kb_contents` ──
  const [contenuti, setContenuti] = useState<KbContenuto[]>([]);
  const [aree, setAree] = useState<KbArea[]>([]);
  const [governance, setGovernance] = useState<KbGovernanceCaricata>({
    governance: KB_GOVERNANCE_DEFAULT,
    origine: 'default',
  });
  const [caricamentoKb, setCaricamentoKb] = useState(true);
  const [erroreKb, setErroreKb] = useState<string | null>(null);

  // La platea si legge a parte: un errore qui non deve impedire di scrivere una
  // bozza, deve solo far dire «non ho potuto contare i destinatari».
  const [platea, setPlatea] = useState<KbPlatea | null>(null);
  const [errorePlatea, setErrorePlatea] = useState<string | null>(null);

  const caricaKb = async () => {
    setCaricamentoKb(true);
    setErroreKb(null);
    try {
      const [listaContenuti, listaAree, gov] = await Promise.all([
        listKbContents(),
        listKbAreas(),
        getKbGovernance(),
      ]);
      setContenuti(listaContenuti);
      setAree(listaAree);
      setGovernance(gov);
    } catch (e) {
      setErroreKb(e instanceof Error ? e.message : 'Errore caricamento dei contenuti');
    } finally {
      setCaricamentoKb(false);
    }
  };

  const caricaPlateaOra = async () => {
    setErrorePlatea(null);
    try {
      setPlatea(await caricaPlatea());
    } catch (e) {
      setPlatea(null);
      setErrorePlatea(e instanceof Error ? e.message : 'errore di lettura di utenti e aziende');
    }
  };

  useEffect(() => {
    caricaKb();
    caricaPlateaOra();
  }, []);

  // ── dati della documentazione video (invariati) ──
  const [chapters, setChapters] = useState<PlatformDocsChapter[]>([]);
  const [videos, setVideos] = useState<PlatformDocsVideo[]>([]);
  const [loadingVideo, setLoadingVideo] = useState(true);
  const [errorVideo, setErrorVideo] = useState<string | null>(null);

  const [chaptersOpen, setChaptersOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formTarget, setFormTarget] = useState<PlatformDocsVideo | null>(null);

  const [statoMenuAnchor, setStatoMenuAnchor] = useState<HTMLElement | null>(null);
  const [statoMenuTarget, setStatoMenuTarget] = useState<PlatformDocsVideo | null>(null);

  // Anteprima inline: un video alla volta. Cache URL per storage_path (non per
  // id: se il file viene sostituito il percorso cambia e l'URL va rifatto).
  const [previewVideoId, setPreviewVideoId] = useState<string | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});

  const loadVideo = async () => {
    setLoadingVideo(true);
    setErrorVideo(null);
    try {
      const [chaptersList, videosList] = await Promise.all([listChapters(), listVideos()]);
      setChapters(chaptersList);
      setVideos(videosList);
    } catch (e) {
      setErrorVideo(e instanceof Error ? e.message : 'Errore caricamento documentazione');
    } finally {
      setLoadingVideo(false);
    }
  };

  useEffect(() => { loadVideo(); }, []);

  const openForm = (target: PlatformDocsVideo | null) => {
    setFormTarget(target);
    setFormOpen(true);
  };

  // ── Raggruppamento per capitolo (ordine capitoli + coda "senza capitolo") ──
  const groups = useMemo(() => {
    const byChapter = new Map<string, PlatformDocsVideo[]>();
    for (const video of videos) {
      const list = byChapter.get(video.chapter_id) ?? [];
      list.push(video);
      byChapter.set(video.chapter_id, list);
    }
    const result: Array<{ chapter: PlatformDocsChapter | null; videos: PlatformDocsVideo[] }> =
      chapters.map((chapter) => ({
        chapter,
        videos: byChapter.get(chapter.id) ?? [],
      }));
    // Video con capitolo inesistente (capitolo cancellato a mano): mai nasconderli
    const knownIds = new Set(chapters.map((c) => c.id));
    const orphans = videos.filter((v) => !knownIds.has(v.chapter_id));
    if (orphans.length > 0) result.push({ chapter: null, videos: orphans });
    return result;
  }, [chapters, videos]);

  const videoCountByChapter = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const video of videos) {
      counts[video.chapter_id] = (counts[video.chapter_id] ?? 0) + 1;
    }
    return counts;
  }, [videos]);

  const areeSuggestions = useMemo(() => distinctAreeCoperte(videos), [videos]);

  // ── Cambio stato rapido ──
  const openStatoMenu = (e: React.MouseEvent<HTMLElement>, video: PlatformDocsVideo) => {
    setStatoMenuAnchor(e.currentTarget);
    setStatoMenuTarget(video);
  };

  const closeStatoMenu = () => {
    setStatoMenuAnchor(null);
    setStatoMenuTarget(null);
  };

  const handleChangeStato = async (stato: VideoDocStato) => {
    const target = statoMenuTarget;
    closeStatoMenu();
    if (!target || target.stato === stato) return;
    try {
      await updateVideo(target.id, { stato });
      showSnack(`Stato aggiornato: ${STATO_VIDEO_LABELS[stato]}`, 'success');
      loadVideo();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Errore aggiornamento stato', 'error');
    }
  };

  // ── Anteprima inline ──
  const togglePreview = async (video: PlatformDocsVideo) => {
    if (previewVideoId === video.id) {
      setPreviewVideoId(null);
      return;
    }
    if (!previewUrls[video.storage_path]) {
      setPreviewLoadingId(video.id);
      try {
        const url = await getVideoDownloadUrl(video.storage_path);
        setPreviewUrls((u) => ({ ...u, [video.storage_path]: url }));
      } catch (e) {
        showSnack(e instanceof Error ? e.message : 'Errore caricamento anteprima', 'error');
        setPreviewLoadingId(null);
        return;
      }
      setPreviewLoadingId(null);
    }
    setPreviewVideoId(video.id);
  };

  const contaBozze = contenuti.filter((c) => c.stato !== 'pubblicato' && c.stato !== 'archiviato').length;
  const contaManutenzione =
    contenutiSenzaOwner(contenuti).length + contenutiScaduti(contenuti, Date.now()).length;

  return (
    <Box>
      {/* HEADER */}
      <Box sx={{ mb: 2.5 }}>
        <Typography
          sx={{
            fontFamily: tokens.font.display,
            fontWeight: 600,
            fontSize: { xs: '1.6rem', md: '1.95rem' },
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: tokens.color.graphite,
            fontVariationSettings: '"WONK" 0, "SOFT" 30',
          }}
        >
          Documentazione
        </Typography>
        <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.5, maxWidth: 720 }}>
          Quello che le aziende clienti leggono per capire il gestionale: gli annunci delle
          funzioni nuove, gli articoli della guida e i video tutorial. La guida non si paga —
          si paga per scrivere i propri contenuti, non per leggere il manuale.
        </Typography>
      </Box>

      <Box sx={{ mb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_e, v: TabAttiva) => setTab(v)}>
          <Tab
            value="contenuti"
            icon={<CampaignOutlined fontSize="small" />}
            iconPosition="start"
            label={contaBozze > 0 ? `Novità e articoli (${contaBozze} in bozza)` : 'Novità e articoli'}
          />
          <Tab
            value="video"
            icon={<VideoLibrary fontSize="small" />}
            iconPosition="start"
            label="Video"
          />
          <Tab
            value="manutenzione"
            icon={<HealthAndSafetyOutlined fontSize="small" />}
            iconPosition="start"
            label={contaManutenzione > 0 ? `Manutenzione (${contaManutenzione})` : 'Manutenzione'}
          />
        </Tabs>
      </Box>

      {tab === 'contenuti' && (
        <TabContenuti
          contenuti={contenuti}
          aree={aree}
          platea={platea}
          errorePlatea={errorePlatea}
          governance={governance}
          caricamento={caricamentoKb}
          errore={erroreKb}
          onRicarica={caricaKb}
          showSnack={showSnack}
        />
      )}

      {tab === 'manutenzione' && (
        <TabManutenzione contenuti={contenuti} platea={platea} caricamento={caricamentoKb} />
      )}

      {/* ==================== TAB VIDEO (invariata) ==================== */}
      {tab === 'video' && (
        <Box>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
            mb={2}
          >
            <Typography variant="body2" sx={{ color: tokens.color.ash, maxWidth: 560 }}>
              Video tutorial del gestionale, organizzati in capitoli. I video "attuali" sono
              visibili ai tenant, quelli "da aggiornare" mostrano un avviso, gli "obsoleti"
              non sono mai visibili.
            </Typography>
            <Stack direction="row" spacing={1.5} useFlexGap alignItems="center" sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1.5 }}>
              <Tooltip title="Ricarica">
                <IconButton onClick={loadVideo} disabled={loadingVideo} size="small">
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button variant="outlined" startIcon={<LibraryBooks />} onClick={() => setChaptersOpen(true)}>
                Capitoli
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => openForm(null)}
                disabled={chapters.length === 0}
              >
                Nuovo video
              </Button>
            </Stack>
          </Stack>

          {errorVideo && <Alert severity="error" sx={{ mb: 2 }}>{errorVideo}</Alert>}

          {chapters.length === 0 && !loadingVideo && !errorVideo && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Per caricare un video crea prima almeno un capitolo con "Capitoli".
            </Alert>
          )}

          {loadingVideo ? (
            <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderColor: tokens.color.line }}>
              <CircularProgress size={28} />
            </Paper>
          ) : (
            <Stack spacing={2.5}>
              {groups.map((group) => (
                <Paper
                  key={group.chapter?.id ?? '__senza_capitolo__'}
                  variant="outlined"
                  sx={{ borderColor: tokens.color.line, bgcolor: tokens.color.paper, overflow: 'hidden' }}
                >
                  {/* Header capitolo */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.25}
                    sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${tokens.color.lineSoft}` }}
                  >
                    <Typography
                      sx={{
                        fontFamily: tokens.font.display,
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        color: tokens.color.graphite,
                      }}
                    >
                      {group.chapter ? group.chapter.titolo : 'Senza capitolo'}
                    </Typography>
                    <SoftChip
                      label={group.videos.length === 1 ? '1 video' : `${group.videos.length} video`}
                      tone={group.videos.length > 0 ? 'info' : 'default'}
                      outlined={group.videos.length === 0}
                    />
                    {group.chapter && !group.chapter.attivo && (
                      <SoftChip label="Capitolo disattivo" tone="default" outlined />
                    )}
                    {!group.chapter && (
                      <Tooltip title="Il capitolo di questi video non esiste più: riassegnali con Modifica">
                        <Box component="span"><SoftChip label="Da riassegnare" tone="warning" /></Box>
                      </Tooltip>
                    )}
                    {group.chapter?.descrizione && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: tokens.color.mist,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {group.chapter.descrizione}
                      </Typography>
                    )}
                  </Stack>

                  {group.videos.length === 0 ? (
                    <Box sx={{ px: 2, py: 2.5 }}>
                      <Typography
                        sx={{
                          fontFamily: tokens.font.display,
                          fontStyle: 'italic',
                          fontSize: '0.95rem',
                          color: tokens.color.mist,
                        }}
                      >
                        Nessun video in questo capitolo.
                      </Typography>
                    </Box>
                  ) : (
                    <Table size="small" sx={{ '& td': { py: 1.25 }, '& tr:last-child td': { borderBottom: 0 } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Titolo</TableCell>
                          <TableCell>Stato</TableCell>
                          <TableCell>Versione</TableCell>
                          <TableCell>Durata</TableCell>
                          <TableCell>Aree coperte</TableCell>
                          <TableCell align="right" sx={{ pr: 2 }}>Azioni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.videos.map((video) => (
                          <React.Fragment key={video.id}>
                            <TableRow hover>
                              <TableCell sx={{ minWidth: 220 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.graphite, lineHeight: 1.3 }}>
                                  {video.titolo}
                                </Typography>
                                {video.descrizione && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: tokens.color.mist,
                                      display: 'block',
                                      maxWidth: 380,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {video.descrizione}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Tooltip title={STATO_VIDEO_HINTS[video.stato]}>
                                  <Box component="span">
                                    <SoftChip
                                      label={STATO_VIDEO_LABELS[video.stato]}
                                      tone={STATO_VIDEO_TONES[video.stato]}
                                    />
                                  </Box>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ fontFamily: tokens.font.mono, color: tokens.color.ash }}>
                                  {video.versione_app || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ fontFamily: tokens.font.mono, color: tokens.color.ash }}>
                                  {formatDurataVideo(video.durata_secondi)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ maxWidth: 260 }}>
                                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                                  {(video.aree_coperte ?? []).map((area) => (
                                    <Chip
                                      key={area}
                                      size="small"
                                      label={area}
                                      variant="outlined"
                                      sx={{ fontFamily: tokens.font.mono, fontSize: '0.65rem', height: 20 }}
                                    />
                                  ))}
                                  {(video.aree_coperte ?? []).length === 0 && (
                                    <Typography variant="caption" sx={{ color: tokens.color.mist }}>—</Typography>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell align="right" sx={{ pr: 2 }}>
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                  <Tooltip title={previewVideoId === video.id ? 'Chiudi anteprima' : 'Anteprima'}>
                                    <IconButton
                                      size="small"
                                      sx={{ color: tokens.color.tierPro.main }}
                                      disabled={previewLoadingId === video.id}
                                      onClick={() => togglePreview(video)}
                                    >
                                      {previewLoadingId === video.id ? (
                                        <CircularProgress size={16} />
                                      ) : previewVideoId === video.id ? (
                                        <StopCircleOutlined fontSize="small" />
                                      ) : (
                                        <PlayCircleOutline fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Cambia stato">
                                    <IconButton size="small" onClick={(e) => openStatoMenu(e, video)}>
                                      <SwapHoriz fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Modifica">
                                    <IconButton size="small" onClick={() => openForm(video)}>
                                      <EditOutlined fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>

                            {/* Anteprima player inline */}
                            {previewVideoId === video.id && previewUrls[video.storage_path] && (
                              <TableRow>
                                <TableCell colSpan={6} sx={{ bgcolor: tokens.color.bone, py: 2 }}>
                                  <Box sx={{ maxWidth: 720, mx: 'auto' }}>
                                    <video
                                      controls
                                      autoPlay
                                      src={previewUrls[video.storage_path]}
                                      style={{ width: '100%', borderRadius: 10, display: 'block' }}
                                    />
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Paper>
              ))}

              {groups.length === 0 && (
                <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderColor: tokens.color.line }}>
                  <Typography
                    sx={{
                      fontFamily: tokens.font.display,
                      fontSize: '1.05rem',
                      fontWeight: 500,
                      fontStyle: 'italic',
                      color: tokens.color.mist,
                    }}
                  >
                    Nessun capitolo: inizia creando la struttura con "Capitoli".
                  </Typography>
                </Paper>
              )}
            </Stack>
          )}

          {/* Menu cambio stato rapido */}
          <Menu anchorEl={statoMenuAnchor} open={!!statoMenuAnchor} onClose={closeStatoMenu}>
            {STATO_VIDEO_ORDER.map((s) => (
              <MenuItem
                key={s}
                selected={statoMenuTarget?.stato === s}
                onClick={() => handleChangeStato(s)}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <SoftChip label={STATO_VIDEO_LABELS[s]} tone={STATO_VIDEO_TONES[s]} />
                  <ListItemText
                    secondary={STATO_VIDEO_HINTS[s]}
                    secondaryTypographyProps={{ fontSize: '0.72rem' }}
                  />
                </Stack>
              </MenuItem>
            ))}
          </Menu>

          {/* Gestione capitoli */}
          <ChaptersDialog
            open={chaptersOpen}
            chapters={chapters}
            videoCountByChapter={videoCountByChapter}
            onClose={() => setChaptersOpen(false)}
            onChanged={loadVideo}
            showSnack={showSnack}
          />

          {/* Crea/modifica video */}
          <VideoFormDialog
            open={formOpen}
            video={formTarget}
            chapters={chapters}
            videos={videos}
            areeSuggestions={areeSuggestions}
            onClose={() => setFormOpen(false)}
            onSaved={() => {
              showSnack(formTarget ? 'Video aggiornato' : 'Video caricato', 'success');
              loadVideo();
            }}
          />
        </Box>
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.msg}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
};
